import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointAdminAuditLogDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import SearchInput from '../../../../../../components/base/input/search_input';
import PageHeader from '../../components/page-header';
import {paginateMockItems} from '../../mock-search';
import {mockAuditLog} from '../../mock-data';

const dataGridSaveConfig = {name: 'BizPointAdminAuditLogDataGrid', version: 2};
const col = BizPointAdminAuditLogDataGridColumnNames;

const searchOptions = {
    keywordFields: ['who', 'what', 'why', 'when'],
    sortFieldMap: {
        [col.when]: 'when',
        [col.who]: 'who',
        [col.what]: 'what',
        [col.why]: 'why',
    },
};

const AdminAuditLogView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.admin.auditLog'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportAdminAuditLog = operatorAccessLevels.packages.bizPoint.exportAdminAuditLog;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-admin-audit-log');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [data, setData] = useState([]);

    /**
     * Configures notebook tags and behavioral analytics for audit log.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointAdminAuditLog);
        configureBehavioralTool((tags) => tags.bizPointAdminAuditLog);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => [
        {
            title: t('when'),
            name: col.when,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            width: 160,
        },
        {title: t('who'), name: col.who, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left, width: 160},
        {title: t('what'), name: col.what, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left, width: 220},
        {
            title: t('why'),
            name: col.why,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            width: 420,
            sortable: false,
        },
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Loads audit log entries from mock data; keyword matches actor, action, and reason.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(mockAuditLog, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(mockAuditLog, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {},
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {
            [col.when]: (item) => item.when,
            [col.why]: (item) => item.why,
        };
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, searchData.filters, searchData.orderBy, t]);

    const rows = useMemo(() => data.map((row, idx) => ({
        key: `${row.when}-${row.who}-${idx}`,
        data: row,
        cells: {
            [col.when]:
                <Typography variant="body2" sx={{whiteSpace: 'nowrap'}}>
                    {row.when}
                </Typography>,
            [col.who]: row.who,
            [col.what]: row.what,
            [col.why]:
                <Typography variant="body2" color="text.secondary" sx={{fontSize: 12, lineHeight: 1.4}}>
                    {row.why}
                </Typography>,
        },
    })), [data]);

    return (
        <Box>
            <PageHeader title={t('title')}/>

            <Box sx={{mb: 3}}>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchByKeyword}
                    initialValue={searchData.filters?.keyword ?? undefined}
                    label={tSearch('keyword')}
                />
            </Box>

            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                getExportInfo={canExportAdminAuditLog ? getExportInfo : undefined}
            />
        </Box>
    );
};

export default AdminAuditLogView;
